import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { BRAND_MINT } from '@/lib/utils'
import { getAdminUsersApi, sendManualEmailApi, sendManualPushApi, type AdminUser } from '@/lib/api'
import {
  LoaderIcon,
  MailIcon,
  BellIcon,
  SendIcon,
  SearchIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
} from 'lucide-react'

const EMAIL_TEMPLATES = [
  { value: 'welcome', label: 'Welcome Email', description: 'Sent after registration.' },
  { value: 'company-welcome', label: 'Company Welcome', description: 'Sent when a company is onboarded.' },
  { value: 'account-created', label: 'Account Created', description: 'Sent when an employee account is created.' },
  { value: 'org-approved', label: 'Org Approved', description: 'Sent when an org verification is approved.' },
  { value: 'org-rejected', label: 'Org Rejected', description: 'Sent when an org verification is rejected.' },
  { value: 'added-to-company', label: 'Added to Company', description: 'Sent when a user is added to an org.' },
]

export const Route = createFileRoute('/dashboard/admin/actions')({
  component: () => {
    // ── User search (for both email + push) ──
    const [searchInput, setSearchInput] = useState('')
    const [searchResults, setSearchResults] = useState<AdminUser[]>([])
    const [searching, setSearching] = useState(false)
    const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
    const [showUserPicker, setShowUserPicker] = useState(false)

    // ── Email state ──
    const [emailTemplate, setEmailTemplate] = useState('welcome')
    const [orgName, setOrgName] = useState('')
    const [sendingEmail, setSendingEmail] = useState(false)
    const [emailResult, setEmailResult] = useState<string | null>(null)

    // ── Push state ──
    const [pushTitle, setPushTitle] = useState('')
    const [pushBody, setPushBody] = useState('')
    const [pushRoute, setPushRoute] = useState('')
    const [sendingPush, setSendingPush] = useState(false)
    const [pushResult, setPushResult] = useState<string | null>(null)

    // ── User search ──
    const handleSearchUsers = async () => {
      if (!searchInput.trim()) return
      setSearching(true)
      try {
        const res = await getAdminUsersApi({ search: searchInput.trim(), limit: 10 })
        setSearchResults(res.data)
        setShowUserPicker(true)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Search failed')
      } finally {
        setSearching(false)
      }
    }

    const handleSelectUser = (user: AdminUser) => {
      setSelectedUser(user)
      setShowUserPicker(false)
      setSearchResults([])
      setSearchInput('')
      setEmailResult(null)
      setPushResult(null)
    }

    // ── Send email ──
    const handleSendEmail = async () => {
      if (!selectedUser) { toast.error('Select a user first.'); return }
      setSendingEmail(true)
      setEmailResult(null)
      try {
        const res = await sendManualEmailApi(
          selectedUser.user_id,
          emailTemplate,
          orgName || undefined,
        )
        setEmailResult(`✅ Email sent${res.skipped ? ' (SMTP not configured — skipped)' : ''}`)
        toast.success(`Email sent to ${selectedUser.email}`)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to send email')
        setEmailResult('❌ Failed to send email')
      } finally {
        setSendingEmail(false)
      }
    }

    // ── Send push ──
    const handleSendPush = async () => {
      if (!selectedUser) { toast.error('Select a user first.'); return }
      if (!pushTitle.trim() || !pushBody.trim()) { toast.error('Title and body are required.'); return }
      setSendingPush(true)
      setPushResult(null)
      try {
        const data = pushRoute.trim()
          ? { route: pushRoute.trim(), source: 'admin_manual' }
          : { route: '/notifications', source: 'admin_manual' }
        const res = await sendManualPushApi(selectedUser.user_id, pushTitle.trim(), pushBody.trim(), data)
        setPushResult(`✅ Delivered to ${res.result.delivered} device(s)`)
        toast.success('Push notification sent.')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to send push')
        setPushResult('❌ Failed to send push notification')
      } finally {
        setSendingPush(false)
      }
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div>
              <h1 className="text-2xl font-semibold">Admin actions</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Manually trigger emails and push notifications.
              </p>
            </div>
          </div>
        </div>

        {/* ── User selector ── */}
        <section className="mb-8 rounded-xl border border-border bg-card p-5">
          <h2 className="mb-3 text-sm font-semibold text-foreground">Target user</h2>
          {selectedUser ? (
            <div className="flex items-center justify-between rounded-lg bg-muted/50 px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex size-9 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                  {selectedUser.first_name?.charAt(0)}{selectedUser.last_name?.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">{selectedUser.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedUser.email} · {selectedUser.role}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>
                Change
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearchUsers() }}
                  placeholder="Search by name, email, or pseudonym…"
                  className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-3 text-sm outline-none focus:border-primary"
                />
              </div>
              <Button size="sm" onClick={handleSearchUsers} disabled={searching} className="gap-1.5">
                {searching ? <LoaderIcon className="size-4 animate-spin" /> : <SearchIcon className="size-4" />}
                Search
              </Button>
            </div>
          )}
        </section>

        {/* ── User picker dialog ── */}
        <Dialog
          open={showUserPicker}
          onClose={() => setShowUserPicker(false)}
          title="Select a user"
          description="Choose the target user for this action."
          footer={
            <Button variant="outline" size="sm" onClick={() => setShowUserPicker(false)}>Cancel</Button>
          }
        >
          <div className="max-h-64 space-y-1 overflow-y-auto py-2">
            {searchResults.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No users found.</p>
            ) : (
              searchResults.map((user) => (
                <button
                  key={user.user_id}
                  type="button"
                  onClick={() => handleSelectUser(user)}
                  className="w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-muted"
                >
                  <p className="text-sm font-medium text-foreground">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email} · {user.org_name || 'No org'}</p>
                </button>
              ))
            )}
          </div>
        </Dialog>

        {selectedUser && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* ── Email panel ── */}
            <section className="rounded-xl border border-border bg-card p-5">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-600">
                  <MailIcon className="size-4" />
                </div>
                <h2 className="text-sm font-semibold text-foreground">Send email</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Email template</label>
                  <select
                    value={emailTemplate}
                    onChange={(e) => setEmailTemplate(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                  >
                    {EMAIL_TEMPLATES.map((tpl) => (
                      <option key={tpl.value} value={tpl.value}>{tpl.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-muted-foreground/60">
                    {EMAIL_TEMPLATES.find((t) => t.value === emailTemplate)?.description}
                  </p>
                </div>

                {(emailTemplate === 'company-welcome' || emailTemplate === 'account-created' || emailTemplate === 'added-to-company') && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-muted-foreground">Organization name</label>
                    <input
                      type="text"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder="e.g. Acme Corp"
                      className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                    />
                  </div>
                )}

                <div className="rounded-lg bg-muted/50 px-3 py-2">
                  <p className="text-[10px] font-medium text-muted-foreground">Recipient</p>
                  <p className="text-sm font-medium text-foreground">{selectedUser.email}</p>
                </div>

                <Button
                  onClick={handleSendEmail}
                  disabled={sendingEmail}
                  className="w-full gap-1.5"
                  size="sm"
                >
                  {sendingEmail ? (
                    <LoaderIcon className="size-4 animate-spin" />
                  ) : (
                    <SendIcon className="size-4" />
                  )}
                  {sendingEmail ? 'Sending…' : 'Send email'}
                </Button>

                {emailResult && (
                  <div className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2">
                    {emailResult.startsWith('✅') ? (
                      <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                    ) : (
                      <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    )}
                    <p className="text-xs text-muted-foreground">{emailResult}</p>
                  </div>
                )}
              </div>
            </section>

            {/* ── Push panel ── */}
            <section className="rounded-xl border border-border bg-card p-5">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex size-8 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
                  <BellIcon className="size-4" />
                </div>
                <h2 className="text-sm font-semibold text-foreground">Send push notification</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Title *</label>
                  <input
                    type="text"
                    value={pushTitle}
                    onChange={(e) => setPushTitle(e.target.value)}
                    placeholder="e.g. Important announcement"
                    className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Body *</label>
                  <textarea
                    value={pushBody}
                    onChange={(e) => setPushBody(e.target.value)}
                    placeholder="Notification message content…"
                    rows={3}
                    className="w-full resize-none rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Deep-link route{' '}
                    <span className="text-muted-foreground/60">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={pushRoute}
                    onChange={(e) => setPushRoute(e.target.value)}
                    placeholder="e.g. /circles/123"
                    className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-mono outline-none focus:border-primary"
                  />
                  <p className="mt-1 text-[10px] text-muted-foreground/60">
                    The app screen to open when the user taps the notification.
                  </p>
                </div>

                <div className="rounded-lg bg-muted/50 px-3 py-2">
                  <p className="text-[10px] font-medium text-muted-foreground">Recipient</p>
                  <p className="text-sm font-medium text-foreground">{selectedUser.name} (#{selectedUser.user_id})</p>
                </div>

                <Button
                  onClick={handleSendPush}
                  disabled={sendingPush || !pushTitle.trim() || !pushBody.trim()}
                  className="w-full gap-1.5"
                  size="sm"
                >
                  {sendingPush ? (
                    <LoaderIcon className="size-4 animate-spin" />
                  ) : (
                    <BellIcon className="size-4" />
                  )}
                  {sendingPush ? 'Sending…' : 'Send push notification'}
                </Button>

                {pushResult && (
                  <div className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2">
                    {pushResult.startsWith('✅') ? (
                      <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                    ) : (
                      <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    )}
                    <p className="text-xs text-muted-foreground">{pushResult}</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    )
  },
})
